import { useEffect } from "react";
import {
  selectCity,
  selectCityFullInfo,
  selectWhitelabel,
} from "@/actions/cities";
import type { City } from "@/apis/city/city-apis";
import type { Whitelabel } from "@/types/reducer-states";
import {
  useAdminDetails,
  useCitiesList,
  useSavedCity,
  useSavedWhitelabel,
  useWhiteLabelCode,
  useWhitelabelsList,
} from "@/v2/stores";
import { useAppDispatch } from "./useTypedRedux";

/** Extended City type that includes whiteLabelId from the legacy API response */
interface CityWithWhitelabel extends Omit<City, "whiteLabelId"> {
  whiteLabelId?: number;
}

const SHAREBIKE_CODE = "sharebike";
const NULL_STRING = "null";
const EMPTY_STRING_JSON = '""';

/**
 * Parses the saved whitelabel value from redux-persist storage.
 * The value may be stringified JSON or a raw number/string.
 * Returns the numeric ID if valid, otherwise null.
 */
function parseWhitelabelId(savedWhitelabel: number | null): number | null {
  if (savedWhitelabel === null) {
    return null;
  }

  // Handle serialized values from redux-persist
  const stringValue = String(savedWhitelabel);
  if (stringValue === NULL_STRING || stringValue === EMPTY_STRING_JSON) {
    return null;
  }

  let value: string | number = savedWhitelabel;

  // Try to parse as JSON first (in case it's stringified)
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      value = parsed;
    } catch {
      // Not JSON, keep original value
    }
  }

  // Try to parse as number
  const parsedNum =
    typeof value === "number" ? value : parseInt(String(value), 10);
  return !isNaN(parsedNum) && parsedNum > 0 ? parsedNum : null;
}

/**
 * Automatically sets user preferences (city and whitelabel) based on admin details.
 *
 * - For city admins: sets the city to their assigned city
 * - For whitelabel admins: sets the whitelabel and first available city
 *
 * This hook should be used at the app root level to ensure preferences
 * are set on initial load.
 *
 * The selected city is held in two store fields: `savedCityFullInfo` (the city
 * record) and `savedCity` (the `cityId=N` string the header and every
 * city-filtered screen read). Both are written together, the way a manual pick
 * in the header does -- writing only one leaves the header showing no city
 * while half the app is already scoped to it, and leaves this hook's own
 * "is a city set" check reading a field it never sets.
 */
export function useAutoSetPreferences(): void {
  const dispatch = useAppDispatch();

  const adminDetails = useAdminDetails();
  const whiteLabelCode = useWhiteLabelCode();

  const citiesList = useCitiesList();
  const whiteLabels = useWhitelabelsList();
  const savedCity = useSavedCity();
  const savedWhitelabel = useSavedWhitelabel();

  useEffect(() => {
    if (!adminDetails || !citiesList || citiesList.length === 0) {
      return;
    }

    // Handle serialized values from redux-persist
    const isCitySet =
      savedCity && savedCity !== NULL_STRING && savedCity !== EMPTY_STRING_JSON;
    const savedWhitelabelId = parseWhitelabelId(savedWhitelabel);

    // Cast citiesList to extended type that includes whiteLabelId
    const cities = citiesList as CityWithWhitelabel[];

    // Handle city admin: always lock to their assigned city
    if (adminDetails.cityId !== null) {
      const expectedCity = `cityId=${adminDetails.cityId}`;
      const userCity = cities.find((city) => city.id === adminDetails.cityId);
      if (userCity && savedCity !== expectedCity) {
        dispatch(selectCityFullInfo(userCity));
        dispatch(selectCity(expectedCity));
      }
    }

    // Handle whitelabel admin (non-sharebike)
    if (whiteLabelCode && whiteLabelCode !== SHAREBIKE_CODE) {
      if (whiteLabels && whiteLabels.length > 0) {
        const whitelabel = whiteLabels.find(
          (wl: Whitelabel) => wl.code === whiteLabelCode
        );
        if (whitelabel) {
          // Set whitelabel if not already correct
          const savedIdNum =
            savedWhitelabelId !== null ? Number(savedWhitelabelId) : null;
          const shouldSetWhitelabel = savedIdNum !== whitelabel.id;

          if (shouldSetWhitelabel) {
            dispatch(selectWhitelabel(whitelabel.id));
          }

          // Set first city for this whitelabel if no city is set
          if (adminDetails.cityId === null && !isCitySet && cities.length > 0) {
            const whitelabelCity = cities.find(
              (city) => city.whiteLabelId === whitelabel.id
            );
            if (whitelabelCity) {
              dispatch(selectCityFullInfo(whitelabelCity));
              dispatch(selectCity(`cityId=${whitelabelCity.id}`));
            }
          }
        }
      }
    }
  }, [
    adminDetails,
    citiesList,
    whiteLabels,
    savedCity,
    savedWhitelabel,
    whiteLabelCode,
    dispatch,
  ]);
}
